/**
 * SC-209: one participant, inside the floating window.
 *
 * **Why this is not `ParticipantVideoSidebar`.** The plan asked for that component to be reused if it fit,
 * and it does not: it takes the LiveKit participant array, the screen-share track list, the whole
 * `VideoControlProps` surface and a sidebar panel position, and derives name, mute and camera state from
 * them. The picture-in-picture model has already made those decisions — a tile here *is* the answer, not the
 * inputs to it — so reusing the sidebar would mean fabricating participant objects to feed back into it.
 * Its sizing is also fixed to the sidebar's tile dimensions, where this one has to fill a grid cell the user
 * is dragging around. Checked and rejected 2026-09-20; the tile below is the smaller lie.
 */

import React from 'react'
import { VideoTrack } from '@livekit/components-react'

import type { PipLastTileSpan, PipTile } from './pipModel'
import { HandIcon, handBadgeProps } from '../../ScreenShare/HandRaisedIndicator'
import {
  CAMERA_WAITING_COLOR,
  CameraWaitingPlaceholder,
  cameraWaitingLabel,
} from '../components/CameraWaitingPlaceholder'
import { useCameraTilePicture } from '../components/useCameraTilePicture'
import { CameraGlyph, CameraOffGlyph, MicOffGlyph } from './pipIcons'
import { CameraOffPicture, cameraOffPicture } from '../cameraOffPicture'
import { selfViewVideoStyle } from '../../../utils/selfViewMirror'
import {
  tileBadgeRowStyle,
  tileBadgeStyle,
  tileHandBadgeStyle,
  tileNameStyle,
  tileOverflowBadgeStyle,
  tilePlaceholderStyle,
  tileSpanStyle,
  tileStyle,
  tileVideoStyle,
} from './pipStyles'

export interface PipTileViewProps {
  tile: PipTile
  /** `controls.videoEnabled`: what we asked our own camera to do, read the way the in-page tile reads it. */
  videoEnabled: boolean
  /** People in proximity with no tile of their own; drawn over this one as `+N`. Only the last tile gets it. */
  overflow?: number
  /** Set on the last tile of an odd set, so the grid has no empty cell. */
  span?: PipLastTileSpan
}

/**
 * SC-415: display-only on purpose. Lowering someone else's hand is an owner action that stays in the tab;
 * with no `onLower` the shared helper yields a labelled image rather than a button.
 */
const { style: handBadgeCursor, ...HAND_BADGE_ATTRS } = handBadgeProps(undefined)
const HAND_BADGE_STYLE = { ...tileHandBadgeStyle, ...handBadgeCursor }

/** The sidebar tile's size: the window's tiles are about that big. */
const WAITING_ICON_PX = 28

/**
 * See-through until it paints, as in the in-page tile (`UNPAINTED_VIDEO_STYLE`): LiveKit's opaque black under
 * the waiting cover would darken it before the cover lifts.
 */
const UNPAINTED_TILE_VIDEO_STYLE: React.CSSProperties = {
  ...tileVideoStyle,
  backgroundColor: 'transparent',
}

const WAITING_BADGE_STYLE: React.CSSProperties = {
  ...tileBadgeStyle,
  background: CAMERA_WAITING_COLOR,
}

export function PipTileView({ tile, videoEnabled, overflow = 0, span = 'none' }: PipTileViewProps) {
  // SC-158: the in-page tile's own decision (`useCameraTilePicture`), so a camera held for its background is
  // covered here exactly when it is covered there: never "Camera disabled" while it waits, and our own
  // never uncovered while it is held. The model's track is only ever a live one, so it goes in as is.
  const { waiting, problem, showsPicture, covered, coverPhase, videoRef } = useCameraTilePicture({
    participantId: tile.participantId,
    isLocal: tile.isLocal,
    videoEnabled,
    cameraTrack: tile.cameraState === 'live' ? tile.cameraTrack : undefined,
  })

  return (
    <div
      style={{ ...tileStyle(tile.isSpeaking), ...tileSpanStyle(span) }}
      data-testid={`pip-tile-${tile.participantId}`}
      data-camera-state={tile.cameraState}
      data-camera-waiting={waiting ? 'true' : undefined}
    >
      {/* A track is only ever in hand while frames are arriving, so this can never be a frozen picture. */}
      {showsPicture && tile.cameraTrack && (
        <VideoTrack
          trackRef={tile.cameraTrack}
          ref={videoRef}
          // The Document PiP window shows only camera tiles; our own is mirrored like the in-page tile.
          style={selfViewVideoStyle(
            tile.isLocal,
            coverPhase === 'covering' ? UNPAINTED_TILE_VIDEO_STYLE : tileVideoStyle
          )}
        />
      )}
      {/* After the video, so it lies on top until the video has painted. */}
      {covered || coverPhase ? (
        <CameraWaitingPlaceholder
          participantId={tile.participantId}
          problem={problem}
          settles={!tile.isLocal}
          phase={coverPhase ?? 'covering'}
          iconSize={WAITING_ICON_PX}
        />
      ) : showsPicture ? null : (
        <div style={tilePlaceholderStyle}>
          {tile.cameraState === 'waiting' ? (
            <span>Connecting…</span>
          ) : (
            // The same picture the in-page tile shows, so the two are plainly the same product.
            <CameraOffPicture
              picture={tile.picture ?? cameraOffPicture({ name: tile.displayName, colourKey: tile.participantId })}
            />
          )}
        </div>
      )}

      {tile.handRaised && (
        <div
          {...HAND_BADGE_ATTRS}
          style={HAND_BADGE_STYLE}
          data-testid={`pip-hand-${tile.participantId}`}
        >
          <HandIcon size={12} />
        </div>
      )}

      <div style={tileBadgeRowStyle}>
        {!tile.micEnabled && (
          <div style={tileBadgeStyle} title="Muted" data-testid={`pip-muted-${tile.participantId}`}>
            <MicOffGlyph size={12} />
          </div>
        )}
        {waiting ? (
          <div
            style={WAITING_BADGE_STYLE}
            title={cameraWaitingLabel(problem)}
            data-testid={`pip-camera-waiting-${tile.participantId}`}
          >
            <CameraGlyph size={12} />
          </div>
        ) : (
          tile.cameraState === 'off' && (
            <div
              style={tileBadgeStyle}
              title="Camera disabled"
              data-testid={`pip-camera-disabled-${tile.participantId}`}
            >
              <CameraOffGlyph size={12} />
            </div>
          )
        )}
      </div>

      <span style={tileNameStyle} data-testid={`pip-name-${tile.participantId}`}>
        {tile.isLocal ? 'You' : tile.displayName}
      </span>

      {overflow > 0 && (
        <div style={tileOverflowBadgeStyle} data-testid="pip-overflow">
          +{overflow}
        </div>
      )}
    </div>
  )
}

export default PipTileView
